import {
  MoreHorizontalIcon,
  PencilIcon,
  PlugZapIcon,
  PowerIcon,
  RefreshCwIcon,
  UnplugIcon,
  WrenchIcon,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type {
  ConnectionProviderView,
  ConnectionView,
} from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';
import {
  LIST_ROW,
  LIST_ROW_NAME,
  LIST_ROW_PHONE_END,
  LIST_ROW_PHONE_GRID,
  LIST_ROW_PHONE_NAME,
  LIST_ROW_PHONE_SECOND,
  LIST_ROW_PHONE_TEXT,
  LIST_ROW_SECONDARY,
  LIST_ROW_TRAILING,
} from '@/shared/components/listRow';

import { useConnectionHealth } from '../hooks/useConnectionHealth';
import { connectionSummaryLine } from './connectionSummaryLine';
import { HealthChip } from './HealthChip';
import { healthState } from './healthState';
import { ProviderLogo } from './ProviderLogo';

export interface ConnectedRowProps {
  workspaceId: string;
  connection: ConnectionView;
  /** Absent for a connection whose catalogue entry no longer exists. */
  provider?: ConnectionProviderView;
  /** Re-runs the live test. */
  onTest: () => void;
  /** Opens the connect sheet on this connection. */
  onEdit: () => void;
  /** Opens the connect sheet with the token ready to replace. */
  onFix: () => void;
  /** Registries only: Enable / Disable in the row menu. */
  onToggleEnabled?: () => void;
  /** Confirms before acting; a registry an image still uses is refused. */
  onDisconnect: () => void;
}

/**
 * One connected provider (spec 229 FR-002): logo, name, what it reaches, its
 * health chip and a row menu. Test comes first in the menu so a connection
 * that was never checked has the way to check it at the top; Fix appears
 * only while the chip says Failing.
 *
 * A connection the catalogue no longer describes keeps its name and offers
 * only Disconnect: nothing else about it can be read.
 */
export function ConnectedRow({
  workspaceId,
  connection,
  provider,
  onTest,
  onEdit,
  onFix,
  onToggleEnabled,
  onDisconnect,
}: ConnectedRowProps) {
  // The same cached query the chip reads; this row only needs to know whether
  // it is failing, so Fix can be offered.
  const live = useConnectionHealth(workspaceId, connection.id);
  const failing =
    provider !== undefined &&
    healthState(
      { status: live.isFetching ? 'pending' : live.status, data: live.data },
      connection.health,
    ).kind === 'failing';

  return (
    <li className={cn(LIST_ROW, LIST_ROW_PHONE_GRID)}>
      {provider ? (
        <ProviderLogo type={connection.type} />
      ) : (
        <PlugZapIcon
          aria-hidden="true"
          strokeWidth={1.5}
          className="text-muted-foreground size-6 shrink-0"
        />
      )}
      <span
        className={cn(
          'flex min-w-0 flex-1 flex-col gap-0.5',
          LIST_ROW_PHONE_TEXT,
        )}
      >
        <span className={cn(LIST_ROW_NAME, LIST_ROW_PHONE_NAME)}>
          {connection.name}
        </span>
        <span
          className={cn(
            LIST_ROW_SECONDARY,
            'max-sm:whitespace-normal max-md:[overflow-wrap:anywhere]',
            LIST_ROW_PHONE_SECOND,
          )}
        >
          {provider
            ? connectionSummaryLine(provider, connection)
            : 'Unknown provider'}
        </span>
      </span>
      <span className={cn(LIST_ROW_TRAILING, LIST_ROW_PHONE_END)}>
        {provider && !connection.enabled ? (
          <Badge variant="secondary" className="shrink-0">
            Disabled
          </Badge>
        ) : null}
        {provider ? (
          <HealthChip workspaceId={workspaceId} connection={connection} />
        ) : null}
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                variant="ghost"
                size="icon"
                className="touch-target shrink-0"
              >
                <MoreHorizontalIcon aria-hidden="true" />
                <span className="sr-only">Actions for {connection.name}</span>
              </Button>
            }
          />
          <DropdownMenuContent align="end">
            {provider ? (
              <>
                <DropdownMenuItem onClick={onTest}>
                  <RefreshCwIcon aria-hidden="true" />
                  Test
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onEdit}>
                  <PencilIcon aria-hidden="true" />
                  Edit
                </DropdownMenuItem>
                {failing ? (
                  <DropdownMenuItem onClick={onFix}>
                    <WrenchIcon aria-hidden="true" />
                    Fix
                  </DropdownMenuItem>
                ) : null}
                {onToggleEnabled ? (
                  <DropdownMenuItem onClick={onToggleEnabled}>
                    <PowerIcon aria-hidden="true" />
                    {connection.enabled ? 'Disable' : 'Enable'}
                  </DropdownMenuItem>
                ) : null}
              </>
            ) : null}
            <DropdownMenuItem variant="destructive" onClick={onDisconnect}>
              <UnplugIcon aria-hidden="true" />
              Disconnect
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </span>
    </li>
  );
}
